import { useEffect, useMemo, useRef, useState } from "react";

const getScrollY = () => {
  if (typeof window === "undefined") return 0;
  return window.scrollY || window.pageYOffset || 0;
};

const ROUTE_RESET_SUPPRESSION_MS = 250;

const buildState = ({
  y = getScrollY(),
  previousY = y,
  delta = 0,
  direction = "none",
  threshold = 24,
  hiddenByScroll = false,
} = {}) => ({
  y,
  previousY,
  delta,
  direction,
  isPastThreshold: y > threshold,
  isScrollingDown: direction === "down",
  isScrollingUp: direction === "up",
  hiddenByScroll,
});

export default function useScrollBehavior({
  enabled = true,
  minDelta = 5,
  resetKey,
  threshold = 24,
} = {}) {
  const [state, setState] = useState(() =>
    buildState({
      threshold,
      hiddenByScroll: false,
    })
  );
  const stateRef = useRef(state);
  const rafIdRef = useRef(null);
  const routeResetUntilRef = useRef(0);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    routeResetUntilRef.current =
      performance.now() + ROUTE_RESET_SUPPRESSION_MS;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const resetToCurrentScroll = () => {
      const y = getScrollY();
      const nextState = buildState({
        y,
        previousY: y,
        threshold,
        hiddenByScroll: false,
      });
      stateRef.current = nextState;
      setState(nextState);
    };

    resetToCurrentScroll();
    const firstFrameId = requestAnimationFrame(() => {
      const secondFrameId = requestAnimationFrame(resetToCurrentScroll);
      rafIdRef.current = secondFrameId;
    });
    rafIdRef.current = firstFrameId;

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [resetKey, threshold]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (!enabled) {
      const y = getScrollY();
      const nextState = buildState({
        y,
        previousY: y,
        threshold,
        hiddenByScroll: false,
      });
      stateRef.current = nextState;
      setState(nextState);
      return undefined;
    }

    const evaluateScroll = () => {
      rafIdRef.current = null;

      const currentY = getScrollY();
      const previous = stateRef.current;
      const now = performance.now();

      if (now < routeResetUntilRef.current) {
        const nextState = buildState({
          y: currentY,
          previousY: currentY,
          threshold,
          hiddenByScroll: false,
        });
        stateRef.current = nextState;
        setState(nextState);
        return;
      }

      const delta = currentY - previous.y;

      if (Math.abs(delta) < minDelta) return;

      const direction = delta > 0 ? "down" : "up";
      const hiddenByScroll = currentY > threshold && direction === "down";
      const nextState = buildState({
        y: currentY,
        previousY: previous.y,
        delta,
        direction,
        threshold,
        hiddenByScroll,
      });

      stateRef.current = nextState;
      setState(nextState);
    };

    const onScroll = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(evaluateScroll);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [enabled, minDelta, threshold]);

  return useMemo(
    () => ({
      ...state,
      threshold,
      minDelta,
    }),
    [minDelta, state, threshold]
  );
}
